import {
  Typography,
  Grid,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  Tab,
  Tabs,
  Divider,
  IconButton,
  Paper,
} from "@mui/material";
import { useState } from "react";
import { ContrattoDetailed } from "../../models/ContrattoModel";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { displayFormatted } from "../../utils/date";
import ContrattoFormComponent from "./ContrattoFormComponent";
import ContractStateHistory from "./ContractStateHistory";
import { useNavigate } from "react-router-dom";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface WithContratto {
  contratto: ContrattoDetailed;
}

const ContrattoNewDetailComponent: React.FC<WithContratto> = ({ contratto }) => {
  const navigator = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const [mainDataEditFormOpen, setMainDataEditFormOpen] =
    useState<boolean>(false);
  const toggleMainDataEditFormOpen = () =>
    setMainDataEditFormOpen(!mainDataEditFormOpen);

  const state2Color = [
    { id: "Proposto", color: "info" as const },
    { id: "Firmato", color: "success" as const },
    { id: "Concluso", color: "success" as const },
    { id: "Rifiutato", color: "error" as const },
    { id: "Scaduto", color: "warning" as const },
  ];

  const colorMap = new Map<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning">();
  state2Color.forEach((data) => {
    colorMap.set(data.id, data.color);
  });

  const statoColor = colorMap.get(contratto.statocontratto.nome) || "default";

  const colorThemeMap: Record<string, string> = {
    warning: "#ed6c02",
    info: "#0288d1",
    default: "#757575",
    primary: "#1976d2",
    error: "#d32f2f",
    success: "#2e7d32",
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const InfoRow: React.FC<{ label: string; value: string }> = ({
    label,
    value,
  }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Header Card */}
      <Card
        elevation={2}
        sx={{ mb: 3, background: "linear-gradient(90deg, #fff9c4 0%, #fdd835 100%)" }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <IconButton
              onClick={() => navigator(-1)}
              sx={{ color: "rgba(0,0,0,0.7)", mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" color="rgba(0,0,0,0.87)" fontWeight={700}>
                {contratto.tipologiacontratto.nome}
              </Typography>
              <Typography variant="subtitle1" color="rgba(0,0,0,0.7)" sx={{ mt: 0.5, fontWeight: 700 }}>
                {contratto.anagraficacliente.personafisica?.cognome}{" "}
                {contratto.anagraficacliente.personafisica?.nome}
              </Typography>
            </Box>
            <Chip
              label={contratto.statocontratto.nome}
              sx={{
                fontWeight: 700,
                fontSize: "2.125rem",
                height: 60,
                px: 3,
                bgcolor: "#f5f5f5",
                color: colorThemeMap[statoColor],
                border: "none",
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Tabs Navigation */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": { fontWeight: 600, fontSize: "0.95rem" },
          }}
        >
          <Tab label="Informazioni Principali" />
          <Tab label="Storico Stati" />
        </Tabs>

        {/* Tab 0: Informazioni Principali */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
              {/* Prima Colonna: Informazioni Base */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2, '& > *:last-child': { mb: 0 } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="h6" color="primary" fontWeight={600}>
                      Informazioni Base
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setMainDataEditFormOpen(true)}
                    >
                      Modifica
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <InfoRow label="Cliente" value={`${contratto.anagraficacliente.personafisica?.cognome} ${contratto.anagraficacliente.personafisica?.nome}`} />
                  <InfoRow label="Stato" value={contratto.statocontratto.nome} />
                  <InfoRow label="Tipologia" value={contratto.tipologiacontratto.nome} />
                  <InfoRow label="Data di Scadenza" value={displayFormatted(contratto.data_scadenza)} />
                  {contratto.tipologiacontratto.id === 1 && (
                    <>
                      <InfoRow label="Quota Fissa" value={contratto.quota !== undefined && contratto.quota !== null ? `€ ${contratto.quota}` : "-"} />
                      <InfoRow label="Percentuale Provvigione" value={contratto.percentuale_provvigione !== undefined && contratto.percentuale_provvigione !== null ? `${contratto.percentuale_provvigione}%` : "-"} />
                    </>
                  )}
                </Card>
              </Grid>

              {/* Seconda Colonna: Dati Aggiuntivi */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2, '& > *:last-child': { mb: 0 } }}>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight={600}>
                    Dati Aggiuntivi
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <InfoRow 
                    label="Documento Associato" 
                    value={
                      contratto.customer_document?.document_number !== undefined
                        ? `${contratto.customer_document?.document_number} (${contratto.customer_document?.released_by})`
                        : "-"
                    } 
                  />
                  <InfoRow label="Conto Associato" value={contratto.bank_account?.iban || "-"} />
                  <InfoRow label="Note" value={contratto.note || "-"} />
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 1: Storico Stati */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Card variant="outlined" sx={{ overflow: "visible" }}>
              <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
                <ContractStateHistory history={contratto.storicostatocontratto} />
              </Box>
            </Card>
          </Box>
        </TabPanel>
      </Paper>

      {/* Modals */}
      <ContrattoFormComponent
        initialData={contratto}
        open={mainDataEditFormOpen}
        toggleOpen={toggleMainDataEditFormOpen}
        editEnabled={true}
      />
    </>
  );
};

export default ContrattoNewDetailComponent;
